import { create } from 'zustand';

export type RenderStyle = 'dark' | 'light';

export interface WorldState {
  timeOfDay: number;
  xrayMode: boolean;
  authorityOverlay: boolean;
  vulnerableFlags: boolean;
  showAllActive: boolean;
  activeZone: string | null;
  renderStyle: RenderStyle;
}

export interface UIState {
  activePanel: string | null;
  breadcrumb: string[];
  persona: string;
  eventMode: 'normal' | 'kiosk' | 'sales' | 'largescreen';
  notifications: Array<{ id: string; type: string; message: string; time: string }>;
}

export interface ScenarioState {
  activeScenario: string | null;
  activeRoute: string | null;
  playing: boolean;
  paused: boolean;
  currentStep: number;
  outcomeMessage: string | null;
}

export interface Settings {
  audioEnabled: boolean;
  reducedMotion: boolean;
  lowFiMode: boolean;
  kioskMode: boolean;
  operatorMode: boolean;
}

export interface CameraState {
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
}

export interface AppStore {
  worldState: WorldState;
  uiState: UIState;
  scenarioState: ScenarioState;
  settings: Settings;
  camera: CameraState;

  // UI
  setPanel: (panel: string) => void;
  closePanel: () => void;
  setPersona: (persona: string) => void;
  setBreadcrumb: (crumbs: string[]) => void;
  setEventMode: (mode: UIState['eventMode']) => void;
  addNotification: (type: string, message: string) => void;

  // World
  toggleXray: () => void;
  toggleAuthority: () => void;
  toggleVulnerable: () => void;
  toggleShowAll: () => void;
  setTimeOfDay: (t: number) => void;
  setRenderStyle: (style: RenderStyle) => void;
  setActiveZone: (zone: string | null) => void;

  // Scenario
  triggerScenario: (id: string) => void;
  stopScenario: () => void;
  setOutcomeMessage: (msg: string | null) => void;
  clearOutcomeMessage: () => void;
  playRoute: (routeId: string) => void;
  nextStep: () => void;

  // Camera
  setCamera: (camera: Partial<CameraState>) => void;

  // Settings
  toggleAudio: () => void;
  toggleReducedMotion: () => void;
  toggleLowFi: () => void;
  toggleKiosk: () => void;
  toggleOperator: () => void;

  // General
  reset: () => void;
}

const initialWorldState: WorldState = {
  timeOfDay: 0.4,
  xrayMode: false,
  authorityOverlay: false,
  vulnerableFlags: false,
  showAllActive: false,
  activeZone: null,
  renderStyle: 'dark',
};

const initialUIState: UIState = {
  activePanel: null,
  breadcrumb: ['North Vale'],
  persona: 'Executive',
  eventMode: 'normal',
  notifications: [],
};

const initialScenarioState: ScenarioState = {
  activeScenario: null,
  activeRoute: null,
  playing: false,
  paused: false,
  currentStep: 0,
  outcomeMessage: null,
};

const initialSettings: Settings = {
  audioEnabled: true,
  reducedMotion: false,
  lowFiMode: false,
  kioskMode: false,
  operatorMode: false,
};

const initialCamera: CameraState = {
  center: [-1.7854, 51.5558],
  zoom: 14,
  pitch: 60,
  bearing: -17,
};

export const useAppStore = create<AppStore>((set) => ({
  worldState: initialWorldState,
  uiState: initialUIState,
  scenarioState: initialScenarioState,
  settings: initialSettings,
  camera: initialCamera,

  // UI Actions
  setPanel: (panel) =>
    set((state) => ({
      uiState: { ...state.uiState, activePanel: panel },
    })),

  closePanel: () =>
    set((state) => ({
      uiState: { ...state.uiState, activePanel: null },
    })),

  setPersona: (persona) =>
    set((state) => ({
      uiState: { ...state.uiState, persona },
    })),

  setBreadcrumb: (crumbs) =>
    set((state) => ({
      uiState: { ...state.uiState, breadcrumb: crumbs },
    })),

  setEventMode: (mode) =>
    set((state) => ({
      uiState: { ...state.uiState, eventMode: mode },
    })),

  addNotification: (type, message) =>
    set((state) => ({
      uiState: {
        ...state.uiState,
        notifications: [
          ...state.uiState.notifications,
          {
            id: `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            type,
            message,
            time: new Date().toISOString(),
          },
        ],
      },
    })),

  // World Actions
  toggleXray: () =>
    set((state) => ({
      worldState: { ...state.worldState, xrayMode: !state.worldState.xrayMode },
    })),

  toggleAuthority: () =>
    set((state) => ({
      worldState: {
        ...state.worldState,
        authorityOverlay: !state.worldState.authorityOverlay,
      },
    })),

  setRenderStyle: (style) => {
    document.documentElement.classList.toggle('dark', style === 'dark');
    set((state) => ({
      worldState: { ...state.worldState, renderStyle: style },
    }));
  },

  toggleVulnerable: () =>
    set((state) => ({
      worldState: {
        ...state.worldState,
        vulnerableFlags: !state.worldState.vulnerableFlags,
      },
    })),

  toggleShowAll: () =>
    set((state) => ({
      worldState: {
        ...state.worldState,
        showAllActive: !state.worldState.showAllActive,
      },
    })),

  setTimeOfDay: (t) =>
    set((state) => ({
      worldState: { ...state.worldState, timeOfDay: Math.max(0, Math.min(1, t)) },
    })),

  setActiveZone: (zone) =>
    set((state) => ({
      worldState: { ...state.worldState, activeZone: zone },
    })),

  // Scenario Actions
  triggerScenario: (id) =>
    set(() => ({
      scenarioState: {
        activeScenario: id,
        activeRoute: null,
        playing: true,
        paused: false,
        currentStep: 0,
        outcomeMessage: null,
      },
    })),

  stopScenario: () =>
    set(() => ({
      scenarioState: initialScenarioState,
    })),


  setOutcomeMessage: (msg) =>
    set((state) => ({
      scenarioState: { ...state.scenarioState, outcomeMessage: msg },
    })),

  clearOutcomeMessage: () =>
    set((state) => ({
      scenarioState: { ...state.scenarioState, outcomeMessage: null },
    })),

  playRoute: (routeId) =>
    set((state) => ({
      scenarioState: {
        ...state.scenarioState,
        activeRoute: routeId,
        playing: true,
        paused: false,
        currentStep: 0,
      },
    })),

  nextStep: () =>
    set((state) => ({
      scenarioState: {
        ...state.scenarioState,
        currentStep: state.scenarioState.currentStep + 1,
      },
    })),

  // Camera Actions
  setCamera: (camera) =>
    set((state) => ({
      camera: { ...state.camera, ...camera },
    })),

  // Settings Actions
  toggleAudio: () =>
    set((state) => ({
      settings: { ...state.settings, audioEnabled: !state.settings.audioEnabled },
    })),

  toggleReducedMotion: () =>
    set((state) => ({
      settings: { ...state.settings, reducedMotion: !state.settings.reducedMotion },
    })),

  toggleLowFi: () =>
    set((state) => ({
      settings: { ...state.settings, lowFiMode: !state.settings.lowFiMode },
    })),

  toggleKiosk: () =>
    set((state) => ({
      settings: { ...state.settings, kioskMode: !state.settings.kioskMode },
    })),

  toggleOperator: () =>
    set((state) => ({
      settings: { ...state.settings, operatorMode: !state.settings.operatorMode },
    })),

  // General
  reset: () =>
    set(() => ({
      worldState: initialWorldState,
      uiState: initialUIState,
      scenarioState: initialScenarioState,
      settings: initialSettings,
      camera: initialCamera,
    })),
}));
